import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  ImageIcon,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { API_BASE, formatApiError } from "@/lib/api";
import { GENDER_OPTIONS, ENGLISH_LEVELS } from "@/lib/status";
import { JobCategoryCombobox } from "@/components/JobCategoryCombobox";
import { toast } from "sonner";
import axios from "axios";
import SimpleForm from "@/pages/SimpleForm";
import { LANGUAGES, tTerms } from "@/lib/terms-i18n";

const STEPS = [
  { id: 0, title: "Personal Info" },
  { id: 1, title: "Job Preferences" },
  { id: 2, title: "Education" },
  { id: 3, title: "Experience" },
  { id: 4, title: "Documents / Resume" },
];

const INITIAL = {
  full_name: "",
  father_name: "",
  gender: "",
  date_of_birth: "",
  age: "",
  marital_status: "",
  phone: "",
  whatsapp: "",
  email: "",
  current_location: "",
  current_location_other: "",
  current_area: "",
  preferred_locations: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  job_category: "",
  job_category_other: "",
  industry_preference: "",
  highest_qualification: "",
  institute: "",
  passing_year: "",
  percentage: "",
  total_experience: "",
  current_company: "",
  current_designation: "",
  current_salary: "",
  expected_salary: "",
  notice_period: "",
  skills: "",
  languages: "",
  english_level: "",
  summary: "",
};

const PHONE_RE = /^(?:\+?91[\s-]?)?[6-9]\d{9}$/;
const PIN_RE = /^\d{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(key, value, all = {}) {
  const v = (value ?? "").toString().trim();
  switch (key) {
    case "full_name":
      if (!v) return "Required";
      if (v.length < 3) return "Too short";
      if (!/[a-zA-Z\u0900-\u097F]/.test(v)) return "Please use letters";
      return null;
    case "phone":
      if (!v) return "Required";
      if (!PHONE_RE.test(v.replace(/\s+/g, "")))
        return "Enter a valid 10-digit Indian mobile (starts 6-9)";
      return null;
    case "whatsapp":
      if (!v) return null;
      if (!PHONE_RE.test(v.replace(/\s+/g, "")))
        return "Enter a valid 10-digit Indian mobile";
      return null;
    case "email":
      if (!v) return null;
      if (!EMAIL_RE.test(v)) return "Invalid email";
      return null;
    case "age": {
      if (!v) return null;
      const n = parseInt(v, 10);
      if (Number.isNaN(n)) return "Numbers only";
      if (n < 16) return "Must be 16 or older";
      if (n > 70) return "Must be 70 or younger";
      return null;
    }
    case "pincode":
      if (!v) return null;
      if (!PIN_RE.test(v)) return "6-digit pincode";
      return null;
    case "expected_salary":
    case "current_salary": {
      if (!v) return null;
      const n = Number(v);
      if (Number.isNaN(n)) return "Numbers only";
      if (n < 1000) return "Too low (₹/month)";
      if (n > 1000000) return "Too high — enter monthly amount";
      return null;
    }
    case "total_experience": {
      if (!v) return null;
      const n = Number(v);
      if (Number.isNaN(n)) return "Numbers only";
      if (n < 0 || n > 50) return "0–50 years";
      return null;
    }
    case "date_of_birth": {
      if (!v) return null;
      const dob = new Date(v);
      if (Number.isNaN(dob.getTime())) return "Invalid date";
      const today = new Date();
      const minAge = new Date(today.getFullYear() - 70, today.getMonth(), today.getDate());
      const maxAge = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());
      if (dob > maxAge) return "Must be 16 or older";
      if (dob < minAge) return "Must be 70 or younger";
      return null;
    }
    default:
      return null;
  }
}

export default function PublicFormPage() {
  const nav = useNavigate();
  const [termsGatePassed, setTermsGatePassed] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [termsLang, setTermsLang] = useState("en");
  const [flowChoice, setFlowChoice] = useState(null); // 'have_cv' | 'no_cv'
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [categories, setCategories] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [hasCvChoice, setHasCvChoice] = useState(null); // 'yes' | 'no'
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [citiesWithAreas, setCitiesWithAreas] = useState([]);
  const [areasForCity, setAreasForCity] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/meta/job-categories`).then((r) => setCategories(r.data));
    axios
      .get(`${API_BASE}/meta/job-category-groups`)
      .then((r) => setCategoryGroups(r.data))
      .catch(() => {});
    axios.get(`${API_BASE}/meta/industries`).then((r) => setIndustries(r.data));
    axios
      .get(`${API_BASE}/meta/areas`)
      .then((r) => setCitiesWithAreas(r.data.cities_with_areas || []))
      .catch(() => {});
  }, []);

  // Fetch areas when current_location is a known city
  useEffect(() => {
    const city = data.current_location;
    if (!city || city === "Other") {
      setAreasForCity([]);
      return;
    }
    axios
      .get(`${API_BASE}/meta/areas`, { params: { city } })
      .then((r) => setAreasForCity(r.data.areas || []))
      .catch(() => setAreasForCity([]));
  }, [data.current_location]);

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));

  // auto compute age from DOB
  useEffect(() => {
    if (data.date_of_birth) {
      const dob = new Date(data.date_of_birth);
      if (!isNaN(dob)) {
        const t = new Date();
        let age = t.getFullYear() - dob.getFullYear();
        const m = t.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && t.getDate() < dob.getDate())) age--;
        if (age >= 0 && age < 120) set("age", String(age));
      }
    }
  }, [data.date_of_birth]);

  const stepValid = () => {
    if (step === 0) {
      const cityOk =
        data.current_location &&
        (data.current_location !== "Other" ||
          (data.current_location_other && data.current_location_other.trim()));
      const fields = [
        "full_name", "phone", "whatsapp", "email", "age",
        "pincode", "date_of_birth",
      ];
      const anyErr = fields.some((f) => validateField(f, data[f], data));
      return (
        data.full_name.trim() &&
        data.phone.trim().length >= 7 &&
        data.gender &&
        cityOk &&
        !anyErr
      );
    }
    if (step === 1)
      return (
        !!data.job_category &&
        (data.job_category !== OTHER_JOB ||
          (data.job_category_other && data.job_category_other.trim())) &&
        !validateField("expected_salary", data.expected_salary)
      );
    if (step === 3)
      return (
        !validateField("total_experience", data.total_experience) &&
        !validateField("current_salary", data.current_salary)
      );
    return true;
  };

  const uploadFile = async (file, purpose) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const { data: r } = await axios.post(`${API_BASE}/upload`, fd);
    return r.path;
  };

  const OTHER_JOB = "Other (please specify)";

  const buildPayload = () => {
    const toFloat = (v) => (v === "" || v == null ? null : Number(v));
    const toInt = (v) => (v === "" || v == null ? null : parseInt(v, 10));
    const finalCity =
      data.current_location === "Other"
        ? data.current_location_other?.trim() || ""
        : data.current_location;
    const finalJob =
      data.job_category === OTHER_JOB
        ? data.job_category_other?.trim() || ""
        : data.job_category;
    return {
      ...data,
      current_location: finalCity,
      current_location_other: undefined, // strip helper field
      job_category: finalJob,
      job_category_other: undefined, // strip helper field
      age: toInt(data.age),
      total_experience: toFloat(data.total_experience) ?? 0,
      current_salary: toFloat(data.current_salary),
      expected_salary: toFloat(data.expected_salary),
      preferred_locations: (data.preferred_locations || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      skills: (data.skills || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      languages: (data.languages || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      email: data.email?.trim() ? data.email.trim() : null,
    };
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      let cv_path = null;
      let photo_path = null;
      let has_cv = false;
      const resume_built = hasCvChoice === "no";

      if (hasCvChoice === "yes") {
        if (!cvFile) {
          toast.error("Please attach your CV file");
          setSubmitting(false);
          return;
        }
        cv_path = await uploadFile(cvFile, "cv");
        has_cv = true;
      }

      if (photoFile) {
        photo_path = await uploadFile(photoFile, "photo");
      }

      const payload = {
        ...buildPayload(),
        cv_path,
        photo_path,
        has_cv,
        resume_built,
        terms_accepted_at: termsAcceptedAt || new Date().toISOString(),
        terms_lang: termsLang,
      };
      const { data: created } = await axios.post(`${API_BASE}/candidates`, payload);

      if (created.returning_candidate) {
        toast.success(
          `Welcome back, ${payload.full_name}! We've updated your profile (submission #${created.submission_count}).`
        );
      }

      // Generate + upload terms acknowledgement PDF
      try {
        const { generateTermsAckPDF } = await import("@/lib/terms-ack");
        const { blob } = generateTermsAckPDF({
          id: created.id,
          full_name: payload.full_name,
          phone: payload.phone,
          email: payload.email,
          job_category: payload.job_category,
          terms_accepted_at: payload.terms_accepted_at,
        });
        const ackFile = new File(
          [blob],
          `tnc-acknowledgement-${(payload.full_name || "candidate").replace(/\s+/g, "_")}.pdf`,
          { type: "application/pdf" }
        );
        const fd = new FormData();
        fd.append("file", ackFile);
        fd.append("purpose", "resume"); // store with other resume artifacts
        const { data: up } = await axios.post(`${API_BASE}/upload`, fd);
        await axios.patch(
          `${API_BASE}/candidates/public/${created.id}?token=${created.edit_token}`,
          { terms_acknowledgement_path: up.path }
        );
      } catch (ackErr) {
        // Non-blocking; submission already succeeded
        console.warn("Terms ack PDF upload failed:", ackErr);
      }

      if (resume_built) {
        nav(`/resume-builder/${created.id}?t=${created.edit_token}`);
      } else {
        nav("/submitted");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  if (!termsGatePassed) {
    return (
      <TermsGate
        onAccept={(lang) => {
          setTermsAcceptedAt(new Date().toISOString());
          setTermsLang(lang || "en");
          setTermsGatePassed(true);
        }}
      />
    );
  }

  if (!flowChoice) {
    return (
      <CVChoiceScreen
        onPick={(choice) => {
          setFlowChoice(choice);
          if (choice === "no_cv") {
            // pre-select the detailed-form CV path for step 5
            setHasCvChoice("no");
          }
        }}
      />
    );
  }

  if (flowChoice === "have_cv") {
    return <SimpleForm termsAcceptedAt={termsAcceptedAt} termsLang={termsLang} />;
  }

  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      {/* Top brand bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <div className="text-xs text-ink-600 hidden sm:block">
            Free candidate registration
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* Step header */}
        <div className="mb-6 animate-fade-in-up">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-ink-900">
            Apply with <span className="text-brand-700">Tulsi Jobs</span>
          </h1>
          <p className="text-sm text-ink-600 mt-1">
            Fill the form to be considered for openings across India. Takes ~3 minutes.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-ink-600 mb-2">
            <span data-testid="step-label">
              Step {step + 1} of {STEPS.length} — {STEPS[step].title}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-brand-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <Card className="tj-card">
          <CardContent className="p-5 md:p-8">
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" testid="fld-full-name" error={validateField("full_name", data.full_name)}>
                  <Input
                    data-testid="input-full-name"
                    value={data.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                  />
                </Field>
                <Field label="Father&apos;s / Mother&apos;s Name">
                  <Input
                    data-testid="input-father-name"
                    value={data.father_name}
                    onChange={(e) => set("father_name", e.target.value)}
                  />
                </Field>
                <Field label="Phone Number *" error={validateField("phone", data.phone)}>
                  <Input
                    data-testid="input-phone"
                    inputMode="tel"
                    maxLength={13}
                    value={data.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="10-digit Indian mobile"
                  />
                </Field>
                <Field label="WhatsApp (if different)" error={validateField("whatsapp", data.whatsapp)}>
                  <Input
                    data-testid="input-whatsapp"
                    inputMode="tel"
                    maxLength={13}
                    value={data.whatsapp}
                    onChange={(e) => set("whatsapp", e.target.value)}
                  />
                </Field>
                <Field label="Email" error={validateField("email", data.email)}>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={data.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </Field>
                <Field label="Gender *">
                  <Select
                    value={data.gender}
                    onValueChange={(v) => set("gender", v)}
                  >
                    <SelectTrigger data-testid="input-gender">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Date of Birth">
                  <Input
                    data-testid="input-dob"
                    type="date"
                    value={data.date_of_birth}
                    onChange={(e) => set("date_of_birth", e.target.value)}
                  />
                </Field>
                <Field label="Age">
                  <Input
                    data-testid="input-age"
                    type="number"
                    value={data.age}
                    onChange={(e) => set("age", e.target.value)}
                  />
                </Field>
                <Field label="Marital Status">
                  <Select
                    value={data.marital_status}
                    onValueChange={(v) => set("marital_status", v)}
                  >
                    <SelectTrigger data-testid="input-marital">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {["Single", "Married", "Divorced", "Widowed"].map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Current Location (City) *">
                  <Select
                    value={data.current_location}
                    onValueChange={(v) =>
                      setData((d) => ({
                        ...d,
                        current_location: v,
                        current_area: "",
                      }))
                    }
                  >
                    <SelectTrigger data-testid="input-location">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent>
                      {citiesWithAreas.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other">Other (type below)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {data.current_location === "Other" && (
                  <Field label="Type your city">
                    <Input
                      data-testid="input-location-other"
                      value={data.current_location_other}
                      onChange={(e) =>
                        set("current_location_other", e.target.value)
                      }
                      placeholder="e.g. Lucknow"
                    />
                  </Field>
                )}
                {areasForCity.length > 0 && (
                  <Field label={`Current Area in ${data.current_location}`}>
                    <Select
                      value={data.current_area}
                      onValueChange={(v) => set("current_area", v)}
                    >
                      <SelectTrigger data-testid="input-area">
                        <SelectValue placeholder="Select your area" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {areasForCity.map((a) => (
                          <SelectItem key={a} value={a}>
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Full Address" full>
                  <Textarea
                    data-testid="input-address"
                    rows={2}
                    value={data.address}
                    onChange={(e) => set("address", e.target.value)}
                  />
                </Field>
                <Field label="City">
                  <Input
                    value={data.city}
                    onChange={(e) => set("city", e.target.value)}
                  />
                </Field>
                <Field label="State">
                  <Input
                    value={data.state}
                    onChange={(e) => set("state", e.target.value)}
                  />
                </Field>
                <Field label="Pincode" error={validateField("pincode", data.pincode)}>
                  <Input
                    value={data.pincode}
                    onChange={(e) => set("pincode", e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                  />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Job Category *" full>
                  <JobCategoryCombobox
                    value={data.job_category}
                    onChange={(v) => set("job_category", v)}
                    groups={categoryGroups}
                    items={categories}
                    testid="input-job-category"
                  />
                </Field>
                {data.job_category === OTHER_JOB && (
                  <Field label="Please specify your role *" full>
                    <Input
                      data-testid="input-job-category-other"
                      value={data.job_category_other}
                      onChange={(e) =>
                        set("job_category_other", e.target.value)
                      }
                      placeholder="e.g. Drone Pilot, Yoga Instructor"
                    />
                  </Field>
                )}
                <Field label="Industry">
                  <Select
                    value={data.industry_preference}
                    onValueChange={(v) => set("industry_preference", v)}
                  >
                    <SelectTrigger data-testid="input-industry">
                      <SelectValue placeholder="Preferred industry" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {industries.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Expected Salary (₹ / month)" error={validateField("expected_salary", data.expected_salary)}>
                  <SalaryChips
                    value={data.expected_salary}
                    onPick={(v) => set("expected_salary", String(v))}
                    testid="expected-chips"
                  />
                  <Input
                    data-testid="input-expected-salary"
                    type="number"
                    step="500"
                    value={data.expected_salary}
                    onChange={(e) => set("expected_salary", e.target.value)}
                    placeholder="or type exact (e.g. 15000)"
                  />
                </Field>
                <Field label="Preferred Locations" full>
                  <Input
                    data-testid="input-preferred-locations"
                    value={data.preferred_locations}
                    onChange={(e) => set("preferred_locations", e.target.value)}
                    placeholder="Comma separated (e.g. Mumbai, Pune, Bengaluru)"
                  />
                </Field>
                <Field label="Languages Known" full>
                  <Input
                    data-testid="input-languages"
                    value={data.languages}
                    onChange={(e) => set("languages", e.target.value)}
                    placeholder="Comma separated (English, Hindi, Marathi…)"
                  />
                </Field>
                <Field label="English Communication Level">
                  <Select
                    value={data.english_level}
                    onValueChange={(v) => set("english_level", v)}
                  >
                    <SelectTrigger data-testid="input-english-level">
                      <SelectValue placeholder="Select your level" />
                    </SelectTrigger>
                    <SelectContent>
                      {ENGLISH_LEVELS.map((l) => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Highest Qualification" full>
                  <Input
                    data-testid="input-qualification"
                    value={data.highest_qualification}
                    onChange={(e) => set("highest_qualification", e.target.value)}
                    placeholder="e.g. B.Com, MBA, BE Mechanical"
                  />
                </Field>
                <Field label="Institute / University" full>
                  <Input
                    data-testid="input-institute"
                    value={data.institute}
                    onChange={(e) => set("institute", e.target.value)}
                  />
                </Field>
                <Field label="Year of Passing">
                  <Input
                    data-testid="input-passing-year"
                    value={data.passing_year}
                    onChange={(e) => set("passing_year", e.target.value)}
                  />
                </Field>
                <Field label="Percentage / CGPA">
                  <Input
                    data-testid="input-percentage"
                    value={data.percentage}
                    onChange={(e) => set("percentage", e.target.value)}
                  />
                </Field>
                <Field label="Skills (comma separated)" full>
                  <Input
                    data-testid="input-skills"
                    value={data.skills}
                    onChange={(e) => set("skills", e.target.value)}
                    placeholder="e.g. Excel, Tally, Communication, Customer Service"
                  />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Total Experience (years)">
                  <Input
                    data-testid="input-total-exp"
                    type="number"
                    step="0.1"
                    value={data.total_experience}
                    onChange={(e) => set("total_experience", e.target.value)}
                  />
                </Field>
                <Field label="Notice Period">
                  <Input
                    data-testid="input-notice"
                    value={data.notice_period}
                    onChange={(e) => set("notice_period", e.target.value)}
                    placeholder="e.g. Immediate, 30 days"
                  />
                </Field>
                <Field label="Current Company">
                  <Input
                    data-testid="input-company"
                    value={data.current_company}
                    onChange={(e) => set("current_company", e.target.value)}
                  />
                </Field>
                <Field label="Current Designation">
                  <Input
                    data-testid="input-designation"
                    value={data.current_designation}
                    onChange={(e) => set("current_designation", e.target.value)}
                  />
                </Field>
                <Field label="Current Salary (₹ / month)" error={validateField("current_salary", data.current_salary)}>
                  <SalaryChips
                    value={data.current_salary}
                    onPick={(v) => set("current_salary", String(v))}
                    testid="current-chips"
                  />
                  <Input
                    data-testid="input-current-salary"
                    type="number"
                    step="500"
                    value={data.current_salary}
                    onChange={(e) => set("current_salary", e.target.value)}
                    placeholder="or type exact (e.g. 12000)"
                  />
                </Field>
                <Field label="Brief Profile Summary" full>
                  <Textarea
                    data-testid="input-summary"
                    rows={4}
                    value={data.summary}
                    onChange={(e) => set("summary", e.target.value)}
                    placeholder="2-3 lines about yourself"
                  />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-ink-800">
                    Do you have a CV / Resume ready?
                  </Label>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <button
                      type="button"
                      data-testid="choice-have-cv"
                      onClick={() => setHasCvChoice("yes")}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        hasCvChoice === "yes"
                          ? "border-brand-600 bg-brand-50"
                          : "border-slate-200 hover:border-brand-300"
                      }`}
                    >
                      <FileText size={20} className="text-brand-700 mb-2" />
                      <div className="font-medium text-ink-900">Yes, I&apos;ll attach</div>
                      <div className="text-xs text-ink-600 mt-1">
                        Upload your existing CV (PDF, DOC, DOCX)
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="choice-build-resume"
                      onClick={() => setHasCvChoice("no")}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        hasCvChoice === "no"
                          ? "border-brand-600 bg-brand-50"
                          : "border-slate-200 hover:border-brand-300"
                      }`}
                    >
                      <Sparkles size={20} className="text-brand-700 mb-2" />
                      <div className="font-medium text-ink-900">
                        Build my resume for free
                      </div>
                      <div className="text-xs text-ink-600 mt-1">
                        Add education, previous companies, certifications &amp; preview. Free to upload — ₹200 to download.
                      </div>
                    </button>
                  </div>
                </div>

                {hasCvChoice === "yes" && (
                  <FileDrop
                    label="Upload CV (PDF / DOC / DOCX)"
                    accept=".pdf,.doc,.docx"
                    icon={<FileText size={20} />}
                    file={cvFile}
                    onChange={setCvFile}
                    testid="upload-cv"
                  />
                )}

                <FileDrop
                  label="Profile Photo (optional, JPG/PNG)"
                  accept="image/*"
                  icon={<ImageIcon size={20} />}
                  file={photoFile}
                  onChange={setPhotoFile}
                  testid="upload-photo"
                />

                <div className="text-xs text-ink-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  Your details will be used by Tulsi Jobs Placement Consultant team only,
                  to match you with suitable openings. We do not share your data with third
                  parties without consent.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nav */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            data-testid="prev-step-btn"
            disabled={step === 0 || submitting}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            <ChevronLeft size={16} className="mr-1" /> Previous
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              data-testid="next-step-btn"
              disabled={!stepValid()}
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              Next <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="submit-form-btn"
              onClick={submit}
              disabled={
                submitting ||
                hasCvChoice === null ||
                (hasCvChoice === "yes" && !cvFile)
              }
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Submitting…
                </>
              ) : hasCvChoice === "no" ? (
                <>
                  Continue to Resume Builder <ChevronRight size={16} className="ml-1" />
                </>
              ) : (
                <>
                  Submit Application <CheckCircle2 size={16} className="ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </main>
      <footer className="text-center text-xs text-ink-600 py-8 space-x-3">
        <span>© {new Date().getFullYear()} Tulsi Jobs Placement Consultant</span>
        <span className="text-ink-400">·</span>
        <a href="/terms" className="text-brand-700 hover:underline" data-testid="footer-terms">Terms</a>
        <span className="text-ink-400">·</span>
        <a href="/refund-policy" className="text-brand-700 hover:underline" data-testid="footer-refund">Refund Policy</a>
      </footer>
    </div>
  );
}

function Field({ label, full, children, error }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs uppercase tracking-[0.14em] text-ink-600 font-medium">
        {label}
      </Label>
      {children}
      {error && (
        <p className="text-[11px] text-rose-700 mt-0.5" data-testid="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

function FileDrop({ label, accept, icon, file, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-ink-800">{label}</Label>
      <label
        data-testid={testid}
        className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors"
      >
        <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
          {icon || <Upload size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-900 truncate">
            {file ? file.name : "Click to choose a file"}
          </div>
          <div className="text-xs text-ink-600">
            {file
              ? `${(file.size / 1024).toFixed(0)} KB · ${file.type || "file"}`
              : "Max 10 MB"}
          </div>
        </div>
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}



function CVChoiceScreen({ onPick }) {
  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <div className="text-xs text-ink-600 hidden sm:block">
            Step 2 of 2 — Choose Path
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        <div className="mb-6 animate-fade-in-up">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-ink-900">
            Do you already have a CV / Resume?
          </h1>
          <p className="text-sm text-ink-600 mt-1">
            Pick the option that fits you. Either way, submitting to Tulsi Jobs
            is free.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <button
            type="button"
            data-testid="pick-have-cv"
            onClick={() => onPick("have_cv")}
            className="text-left p-6 rounded-2xl border-2 border-slate-200 bg-white hover:border-brand-600 hover:shadow-md transition-all group"
          >
            <div className="h-12 w-12 rounded-xl bg-brand-50 text-brand-700 grid place-items-center mb-4 group-hover:bg-brand-100">
              <FileText size={22} />
            </div>
            <div className="font-heading text-lg font-semibold text-ink-900">
              Yes, I have a CV
            </div>
            <p className="text-sm text-ink-600 mt-1">
              Quick form — just the basics + upload your CV &amp; photo. Takes ~1 minute.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-700">
              Continue with CV upload <ChevronRight size={14} />
            </div>
          </button>

          <button
            type="button"
            data-testid="pick-no-cv"
            onClick={() => onPick("no_cv")}
            className="text-left p-6 rounded-2xl border-2 border-slate-200 bg-white hover:border-brand-600 hover:shadow-md transition-all group"
          >
            <div className="h-12 w-12 rounded-xl bg-brand-50 text-brand-700 grid place-items-center mb-4 group-hover:bg-brand-100">
              <Sparkles size={22} />
            </div>
            <div className="font-heading text-lg font-semibold text-ink-900">
              No, help me build one
            </div>
            <p className="text-sm text-ink-600 mt-1">
              Full detailed form — we&apos;ll help you create a professional
              resume PDF from your details. Takes ~3 minutes.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-700">
              Continue to detailed form <ChevronRight size={14} />
            </div>
          </button>
        </div>

        <p className="text-xs text-ink-600 mt-8 text-center">
          Optional ₹200 fee only if you want to download the built-resume PDF
          at the end. Submitting to recruiters is always free.
        </p>
      </main>
      <footer className="text-center text-xs text-ink-600 py-8 space-x-3">
        <span>© {new Date().getFullYear()} Tulsi Jobs Placement Consultant</span>
        <span className="text-ink-400">·</span>
        <a href="/terms" className="text-brand-700 hover:underline">Terms</a>
        <span className="text-ink-400">·</span>
        <a href="/refund-policy" className="text-brand-700 hover:underline">Refund Policy</a>
      </footer>
    </div>
  );
}

function TermsGate({ onAccept }) {
  const [agreed, setAgreed] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [lang, setLang] = useState(
    () => localStorage.getItem("tj_terms_lang") || "en"
  );
  const t = tTerms(lang);

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem("tj_terms_lang", code);
    // reset scroll gate when language changes so the user re-reads
    setScrolledToEnd(false);
    setAgreed(false);
    const box = document.querySelector('[data-testid="terms-scroll-box"]');
    if (box) box.scrollTop = 0;
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 32) {
      setScrolledToEnd(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <div className="text-xs text-ink-600 hidden sm:block">
            {t.step_label}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* Language selector */}
        <div
          data-testid="terms-lang-selector"
          className="mb-4 flex flex-wrap items-center gap-2"
        >
          <span className="text-xs uppercase tracking-[0.14em] text-ink-600 mr-1">
            {t.choose_language}:
          </span>
          {LANGUAGES.map((l) => {
            const active = lang === l.code;
            return (
              <button
                key={l.code}
                type="button"
                data-testid={`terms-lang-${l.code}`}
                onClick={() => changeLang(l.code)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? "bg-brand-700 text-white border-brand-700"
                    : "bg-white text-ink-700 border-slate-300 hover:border-brand-400 hover:bg-brand-50"
                }`}
              >
                {l.label}
              </button>
            );
          })}
        </div>

        <div className="mb-6 animate-fade-in-up">
          <h1
            data-testid="terms-heading"
            lang={lang}
            className="font-heading text-2xl md:text-3xl font-semibold text-ink-900"
          >
            {t.h1}
          </h1>
          <p lang={lang} className="text-sm text-ink-600 mt-1">
            {t.subheading}
          </p>
        </div>

        <Card className="tj-card">
          <CardContent className="p-0">
            <div
              data-testid="terms-scroll-box"
              onScroll={onScroll}
              lang={lang}
              className="max-h-[60vh] overflow-y-auto p-6 md:p-8 text-sm text-ink-800 leading-relaxed space-y-4"
            >
              <h2 className="font-heading text-lg font-semibold text-ink-900">
                {t.doc_title}
              </h2>
              <p>{t.intro}</p>

              {t.sections.map((s) => (
                <TGSec key={s.n} n={s.n} title={s.title}>
                  {renderBold(s.body)}
                </TGSec>
              ))}
              {t.subs.map((s) => (
                <TGSub key={s.title} title={s.title}>
                  {renderBold(s.body)}
                </TGSub>
              ))}
              {t.sections_after.map((s) => (
                <TGSec key={s.n} n={s.n} title={s.title}>
                  {renderBold(s.body)}
                </TGSec>
              ))}

              <p className="text-xs text-ink-600 pt-2 border-t border-slate-200">
                {t.full_version_note}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-brand-700"
                >
                  /terms
                </a>
                .
              </p>
            </div>

            <div className="border-t border-slate-200 p-5 md:p-6 bg-slate-50/60">
              <label
                className={`flex items-start gap-3 ${
                  scrolledToEnd ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid="terms-gate-checkbox"
                  disabled={!scrolledToEnd}
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-brand-400 text-brand-700 focus:ring-brand-600"
                />
                <span lang={lang} className="text-sm text-ink-800 leading-relaxed">
                  {renderBold(t.agree_label)}
                  {!scrolledToEnd && (
                    <span className="block mt-1 text-xs text-amber-700">
                      {t.scroll_hint}
                    </span>
                  )}
                </span>
              </label>

              <Button
                data-testid="accept-terms-btn"
                disabled={!agreed}
                onClick={() => onAccept(lang)}
                className="w-full mt-4 bg-brand-700 hover:bg-brand-800 text-white"
              >
                <span lang={lang}>{t.accept_button}</span>{" "}
                <ChevronRight size={16} className="ml-1" />
              </Button>
              <p
                lang={lang}
                className="text-[11px] text-ink-600 text-center mt-3"
              >
                {t.timestamp_note}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
      <footer className="text-center text-xs text-ink-600 py-8 space-x-3">
        <span>© {new Date().getFullYear()} Tulsi Jobs Placement Consultant</span>
        <span className="text-ink-400">·</span>
        <a href="/terms" className="text-brand-700 hover:underline">Terms</a>
        <span className="text-ink-400">·</span>
        <a href="/refund-policy" className="text-brand-700 hover:underline">Refund Policy</a>
      </footer>
    </div>
  );
}

function renderBold(text) {
  // Splits on **markers** and wraps odd segments in <strong>
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function TGSec({ n, title, children }) {
  return (
    <div>
      <h3 className="font-heading font-semibold text-ink-900 text-sm">
        {n} {title}
      </h3>
      <p className="mt-1 text-ink-700">{children}</p>
    </div>
  );
}

function TGSub({ title, children }) {
  return (
    <div className="pl-4 border-l-2 border-brand-200">
      <h4 className="text-sm font-medium text-ink-900">{title}</h4>
      <p className="mt-0.5 text-ink-700">{children}</p>
    </div>
  );
}


const SALARY_CHIP_TIERS = [
  { label: "< ₹10K", value: 8000 },
  { label: "₹10–15K", value: 12500 },
  { label: "₹15–25K", value: 20000 },
  { label: "₹25–40K", value: 32000 },
  { label: "₹40K+", value: 50000 },
];

function SalaryChips({ value, onPick, testid }) {
  const numeric = value === "" ? null : Number(value);
  const isPicked = (v) => {
    if (numeric == null) return false;
    if (v === 50000) return numeric >= 40000;
    if (v === 8000) return numeric < 10000;
    if (v === 12500) return numeric >= 10000 && numeric < 15000;
    if (v === 20000) return numeric >= 15000 && numeric < 25000;
    if (v === 32000) return numeric >= 25000 && numeric < 40000;
    return false;
  };
  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={testid}>
      {SALARY_CHIP_TIERS.map((t) => (
        <button
          type="button"
          key={t.value}
          onClick={() => onPick(t.value)}
          data-testid={`${testid}-${t.value}`}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            isPicked(t.value)
              ? "bg-brand-700 text-white border-brand-700"
              : "bg-white text-ink-700 border-slate-300 hover:border-brand-400 hover:bg-brand-50"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
